import { useEffect, useRef } from 'react';
import { useDebounceCallback, useEventCallback, useUnmount } from 'usehooks-ts';
import { useMediaQuery, useTheme } from '@mui/material';
import { OrientationLockType } from '@capacitor/screen-orientation';
import { Map as GLMap } from 'shared/map-exports';
import { useDeviceOrientation } from 'hooks/useScreenOrientation';
import { useWaitForElement } from 'hooks/useWaitForElement';
import { useDrawerHeightListener } from 'features/mapping/hooks/useDrawerHeightListener';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import { isUserInitiatedMove } from 'features/mapping/mapping.utils';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useMapEntityDrawerState } from 'features/mapping/hooks/useMapEntityDrawerState';
import {
  MAP_ENTITY_DRAWER_WIDTH,
  MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID,
} from 'features/mapping/components/MapEntityDrawer';
import {
  getSearchCardMinSnapPx,
  RESIZE_DEBOUNCE_MS,
  SEARCH_DRAWER_WIDTH,
} from '../../../../constants';

type CapturedView = {
  center: { lng: number; lat: number };
  zoom: number;
};

type NormalizedOrientation = 'portrait' | 'landscape';

const normalizeDeviceOrientation = (
  orientation: OrientationLockType,
): NormalizedOrientation | null => {
  // Case-insensitive match on the orientation string.
  const normalized = orientation.toLowerCase();
  if (normalized.includes('portrait')) return 'portrait';
  if (normalized.includes('landscape')) return 'landscape';
  return null;
};

/**
 * Keeps the user pointed at the same place across a device rotation: snapshots the
 * geographic point at the centre of the visible area (container minus drawer
 * overlay) when the OS reports the rotation, then restores it once layout settles.
 */
export const useRecenterMapOnRotation = (
  map: GLMap | null | undefined,
): void => {
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const { open: isSearchDrawerOpen } = useSearchDrawerState();
  const { open: isEntityDrawerOpen } = useMapEntityDrawerState();

  const bottomSheetHeightRef = useRef(0);
  const capturedViewRef = useRef<CapturedView | null>(null);
  const rotationPendingRef = useRef(false);
  const settledOverlayOffsetRef = useRef<[number, number]>([0, 0]);

  const getOverlayOffset = useEventCallback((): [number, number] => {
    if (isLargeMediaQuery) {
      if (!isEntityDrawerOpen) return [0, 0];
      const coveredWidth = isSearchDrawerOpen
        ? MAP_ENTITY_DRAWER_WIDTH - SEARCH_DRAWER_WIDTH
        : MAP_ENTITY_DRAWER_WIDTH;
      return [-coveredWidth / 2, 0];
    }

    const coveredHeight = isEntityDrawerOpen
      ? bottomSheetHeightRef.current
      : getSearchCardMinSnapPx();
    return [0, -coveredHeight / 2];
  });

  // Frozen while a rotation is in flight so the baseline reflects pre-rotation
  // geometry. On iOS the orientation event fires before layout updates, so
  // measuring at capture time would return the post-rotation dimensions.
  const syncSettledOverlayOffset = useEventCallback((): void => {
    if (rotationPendingRef.current) return;
    settledOverlayOffsetRef.current = getOverlayOffset();
  });

  // Debounced to prevent a layout update from overwriting the baseline
  // before the OS fires the orientation event.
  const syncSettledOverlayOffsetWhenIdle = useDebounceCallback(
    syncSettledOverlayOffset,
    RESIZE_DEBOUNCE_MS,
  );

  const captureView = useEventCallback((): void => {
    if (!map) return;
    const centerPoint = map.project(map.getCenter());
    const [xOffset, yOffset] = settledOverlayOffsetRef.current;
    const focalPoint = map.unproject([
      centerPoint.x + xOffset,
      centerPoint.y + yOffset,
    ]);

    capturedViewRef.current = {
      center: { lng: focalPoint.lng, lat: focalPoint.lat },
      zoom: map.getZoom(),
    };
  });

  const restoreView = useEventCallback((): void => {
    if (!map) return;
    const view = capturedViewRef.current;
    if (!view) return;

    // Resize first: the offset below is in current-viewport pixels.
    map.resize();
    map.stop();

    // `duration: 0` is load-bearing: mapbox resolves `center + offset` at call
    // time, so an animated transition lets a concurrent resize introduce drift.
    map.easeTo({
      center: [view.center.lng, view.center.lat],
      zoom: view.zoom,
      duration: 0,
      pitch: map.getPitch(),
      offset: getOverlayOffset(),
    });
  });

  const restoreWhenLayoutSettles = useDebounceCallback(
    useEventCallback((): void => {
      if (!rotationPendingRef.current) return;
      rotationPendingRef.current = false;
      restoreView();
      capturedViewRef.current = null;
      syncSettledOverlayOffsetWhenIdle.cancel();
      syncSettledOverlayOffset();
    }),
    RESIZE_DEBOUNCE_MS,
  );

  const cancelPendingRotation = useEventCallback((): void => {
    if (!rotationPendingRef.current) return;
    rotationPendingRef.current = false;
    capturedViewRef.current = null;
    restoreWhenLayoutSettles.cancel();
    syncSettledOverlayOffsetWhenIdle.cancel();
    syncSettledOverlayOffset();
  });

  const onGeometryChanged = useEventCallback((): void => {
    if (rotationPendingRef.current) {
      restoreWhenLayoutSettles();
      return;
    }
    syncSettledOverlayOffsetWhenIdle();
  });

  // A user gesture cancels the restore.
  useMapEvent(
    'movestart',
    (event) => {
      if (!rotationPendingRef.current) return;
      if (!isUserInitiatedMove(event)) return;
      cancelPendingRotation();
    },
    map,
    true,
  );

  useUnmount(cancelPendingRotation);

  const sheetElement = useWaitForElement(
    !isLargeMediaQuery && isEntityDrawerOpen
      ? `#${MAP_ENTITY_DRAWER_MOBILE_DRAWER_ID}`
      : null,
  );

  useDrawerHeightListener(sheetElement, null, (heightPx) => {
    bottomSheetHeightRef.current = heightPx;
    onGeometryChanged();
  });

  // Observe map container resizes - covers desktop drawer transitions and the
  // rotation itself.
  useEffect(() => {
    if (!map || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(onGeometryChanged);
    observer.observe(map.getContainer());

    return () => observer.disconnect();
  }, [map, onGeometryChanged]);

  // Drawer/breakpoint changes don't emit a geometry event; also seeds on mount.
  useEffect(syncSettledOverlayOffset, [
    syncSettledOverlayOffset,
    isLargeMediaQuery,
    isEntityDrawerOpen,
    isSearchDrawerOpen,
  ]);

  const previousOrientationRef = useRef<NormalizedOrientation | null>(null);

  const handleNativeOrientationChange = useEventCallback(
    (rawOrientation: OrientationLockType): void => {
      const nextOrientation = normalizeDeviceOrientation(rawOrientation);
      if (!nextOrientation) return;

      const previousOrientation = previousOrientationRef.current;
      previousOrientationRef.current = nextOrientation;
      if (previousOrientation === nextOrientation) return;

      // Only capture on the first event of a burst - a later one could snapshot
      // an intermediate layout state.
      if (!rotationPendingRef.current) {
        captureView();
        if (!capturedViewRef.current) return;
      }

      rotationPendingRef.current = true;
      restoreWhenLayoutSettles();
    },
  );

  const deviceOrientation = normalizeDeviceOrientation(
    useDeviceOrientation(handleNativeOrientationChange),
  );

  // Seed the comparison ref so the first orientation event isn't mistaken for a rotation.
  useEffect(() => {
    if (deviceOrientation) previousOrientationRef.current = deviceOrientation;
  }, [deviceOrientation]);
};
